import React, { FunctionComponent } from 'react';
import { CheckboxWithLabel } from '../CheckboxWithLabel';
import { Label } from '../Label';
import { EvaluationEntry, MultipleEvaluations } from '../MultipleEvaluations';
import { EvaluationValue } from '../EvaluationSelector';
import { ImageDxPointValue, PointWithConditions } from './imageDxTypes';
import { DiagnosisOptionSets } from '../ImageDiagnosis';

export interface PainAnalysisDialogProps {
  // point contains information about the point on the image, including its name and relevant conditions.
  point: PointWithConditions;

  // evaluations is the list of evaluations (e.g. "Pain upon manipulation") for the point.
  evaluations: EvaluationEntry[];

  // initialEvaluationValue is the default value to use for all the evaluations (i.e. the pain levels).
  initialEvaluationValue: number;

  // currentPointValues contains the current values for all points on the image.
  currentPointValues: Record<string, ImageDxPointValue>;

  // conditionsLabel and conditionsDescription are the labels and descriptions for the conditions section.
  conditionsLabel?: string;
  conditionsDescription?: string;

  // onConditionChange is called when the user changes the conditions for a point.
  onConditionChange?: (pointName: string, conditionName: string, checked: boolean) => void;

  // onEvaluationsChange is called when the user changes any of the evaluation values on a given point (specified by
  // pointName).
  onEvaluationsChange?: (pointName: string, evaluations: Record<string, EvaluationValue>) => void;
}

// PainAnalysisDialog is the dialog that's shown when the user clicks on a point on the image.  It has two parts:
// 1. The pain levels for the point (one or more evaluations like "Pain upon manipulation").
// 2. The conditions that are affecting the point (like "Orthopedic: OA").
export const PainAnalysisDialog: FunctionComponent<PainAnalysisDialogProps> = ({
  point,
  evaluations,
  currentPointValues,
  conditionsLabel = 'Conditions',
  conditionsDescription = 'Select all the conditions that are affecting gait at this location.',
  onConditionChange = () => {},
  onEvaluationsChange = () => {}
}) => {
  // isChecked is a helper function to check if a condition is checked for a given point.
  const isChecked = (pointName: string, pointCondition: string): boolean => {
    const pointValue = currentPointValues?.[pointName];
    return !!(pointValue && pointValue.conditions && pointValue.conditions.includes(pointCondition));
  };

  return (
    <div>
      <MultipleEvaluations
        name={point.name}
        evaluations={evaluations}
        values={currentPointValues?.[point.name]?.evaluations}
        options={DiagnosisOptionSets.painLocationOptions}
        onChange={(values) => {
          onEvaluationsChange(point.name, values);
        }}
        defaultValue={0}
      />
      <div className="mt-6 flex flex-col gap-y-4">
        <Label description={conditionsDescription}>{conditionsLabel}</Label>
        <div className="grid grid-cols-2 gap-4">
          {point.conditions.map(({ label: conditionLabel, value: conditionValue }) => (
            <CheckboxWithLabel
              key={`dx-for-location-${point.name}-${conditionValue}`}
              id={`dx-for-location-${point.name}-${conditionValue}`}
              name={`${point.name}-${conditionValue}`}
              label={conditionLabel}
              checked={isChecked(point.name, conditionValue)}
              onCheckedChange={(checked) => {
                onConditionChange(point.name, conditionValue, !!checked);
              }}
              className="max-w-lg"
            />
          ))}
          <CheckboxWithLabel
            key={`dx-for-location-${point.name}-other`}
            id={`dx-for-location-${point.name}-other`}
            name={`${point.name}-other`}
            label="Other"
            checked={isChecked(point.name, 'other')}
            onCheckedChange={(checked) => {
              onConditionChange(point.name, 'other', !!checked);
            }}
            className="max-w-lg"
          />
        </div>
      </div>
    </div>
  );
};
